'use client';

import { useRouter } from 'next/navigation';
import { useState, useRef, useEffect } from 'react';
import { useSearch } from '@/hooks/useSearch';
import NotificationBell from './NotificationBell';

interface SearchResult {
  id: number;
  title: string;
  description?: string;
  easy_id?: string;
  dashboard?: number;
  dashboard_display?: string;
  project?: number;
  status?: number;
  status_display?: string;
}

export default function Header() {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const { data: searchResults = [], isLoading } = useSearch({
    query: searchQuery,
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTaskClick = (taskId: number) => {
    // TODO: Open task modal when TaskModal is refactored to be accessible globally
    console.log('Open task:', taskId);
    setShowResults(false);
    setSearchQuery('');
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    router.push('/login');
  };

  return (
    <header className="w-full bg-[#0f1429] border-b border-[#1a1f3a] px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-2xl font-semibold text-gray-400 tracking-tight">GENESIS</span>
      </div>

      <div className="flex-1 flex justify-center">
        <div className="relative" ref={searchRef}>
          <input
            type="text"
            placeholder="Q Search..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
            className="bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg px-4 py-2 pl-10 text-white placeholder-gray-400 focus:outline-none focus:border-orange-500 w-64 text-sm"
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>

          {/* Search Results Dropdown */}
          {showResults && searchQuery.length >= 2 && (
            <div className="absolute top-full mt-2 w-96 bg-[#1a1f3a] border border-[#2d1b3d] rounded-lg shadow-xl max-h-96 overflow-y-auto z-50">
              {isLoading ? (
                <div className="p-4 text-gray-400 text-sm">Searching...</div>
              ) : searchResults.length === 0 ? (
                <div className="p-4 text-gray-400 text-sm">No results found</div>
              ) : (
                <div className="py-2">
                  {searchResults.map((task: SearchResult) => (
                    <button
                      key={task.id}
                      onClick={() => handleTaskClick(task.id)}
                      className="w-full px-4 py-3 hover:bg-[#2d1b3d] text-left transition-colors border-b border-[#2d1b3d] last:border-b-0"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1">
                          <div className="text-white font-medium text-sm">{task.title}</div>
                          {task.description && (
                            <div className="text-gray-400 text-xs mt-1 line-clamp-2">
                              {task.description}
                            </div>
                          )}
                          <div className="flex items-center gap-2 mt-2">
                            {task.easy_id && (
                              <span className="text-xs text-gray-500">#{task.easy_id}</span>
                            )}
                            {task.project && (
                              <span className="text-xs text-blue-400">Project</span>
                            )}
                            {task.dashboard_display && (
                              <span className="text-xs text-purple-400">{task.dashboard_display}</span>
                            )}
                            {task.status_display && (
                              <span className="text-xs text-gray-500">{task.status_display}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <NotificationBell />
        <button className="p-2 bg-[#1a1f3a] rounded-lg hover:bg-[#2d1b3d] transition-colors">
          <svg
            className="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
        </button>
        <button
          onClick={handleLogout}
          className="px-4 py-2 bg-[#1a1f3a] rounded-lg hover:bg-[#2d1b3d] transition-colors text-white font-medium"
        >
          Logout
        </button>
      </div>
    </header>
  );
}

